/**
 * Data-driven Safety Scans list Result / Verification presentation.
 * Each row reads that Scan's persisted fields. No specimen constants.
 */

export type ScanBuyerResultInput = {
  measuredOccurrenceCount?: number | null;
  reviewedOccurrenceCount?: number | null;
  confirmedOccurrenceCount?: number | null;
  usableHours?: number | null;
  coverageState?: string | null;
  verificationState?: string | null;
};

export type ScanBuyerResultView = {
  primary: string;
  secondary: string | null;
  tertiary: string | null;
  coverageLabel: string | null;
  verificationLabel: string;
  needsReview: boolean;
};

function finiteInt(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  return rounded >= 0 ? rounded : null;
}

function finiteHours(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return value;
}

function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

function formatHoursNumber(hours: number): string {
  if (Number.isInteger(hours)) return String(hours);
  return String(Number.parseFloat(hours.toFixed(2)));
}

function normalizeToken(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
}

export function mapBuyerCoverageLabel(
  coverageState?: string | null,
  usableHours?: number | null,
): string | null {
  const token = normalizeToken(coverageState);
  if (token === 'full' || token === 'covered' || token === 'sufficient' || token === 'complete') {
    return 'Full coverage';
  }
  if (token === 'partial' || token === 'incomplete') {
    return 'Partial coverage';
  }
  if (
    token === 'unavailable'
    || token === 'missing'
    || token === 'none'
    || token === 'no_usable_measurement'
  ) {
    return 'No usable measurement';
  }
  if (usableHours === 0) return 'No usable measurement';
  return null;
}

export function mapBuyerScanVerificationLabel(verificationState?: string | null): string {
  const token = normalizeToken(verificationState);
  if (
    token === 'verified'
    || token === 'complete'
    || token === 'completed'
    || token === 'frozen'
    || token === 'review_complete'
  ) {
    return 'Verified';
  }
  if (
    token === 'needs_review'
    || token === 'review_required'
    || token === 'pending'
    || token === 'in_progress'
    || token === 'open'
    || token === 'draft'
  ) {
    return 'Needs review';
  }
  return '';
}

export function formatBuyerScanResultHierarchy(input: ScanBuyerResultInput): ScanBuyerResultView {
  const measured = finiteInt(input.measuredOccurrenceCount);
  const reviewed = finiteInt(input.reviewedOccurrenceCount);
  const confirmed = finiteInt(input.confirmedOccurrenceCount);
  const hours = finiteHours(input.usableHours);
  const coverageLabel = mapBuyerCoverageLabel(input.coverageState, hours);
  const verificationLabel = mapBuyerScanVerificationLabel(input.verificationState);
  const needsReview = verificationLabel === 'Needs review';

  let primary = '';
  if (measured != null) {
    primary = `${formatCount(measured)} measured occurrence${measured === 1 ? '' : 's'}`;
  } else if (confirmed != null) {
    primary = `${formatCount(confirmed)} confirmed observation${confirmed === 1 ? '' : 's'}`;
  } else if (coverageLabel === 'No usable measurement' || hours === 0) {
    primary = 'No usable measurement';
  } else {
    primary = '—';
  }

  let secondary: string | null = null;
  if (reviewed != null && confirmed != null) {
    secondary = `${formatCount(reviewed)} reviewed · ${formatCount(confirmed)} confirmed`;
  } else if (reviewed != null) {
    secondary = `${formatCount(reviewed)} reviewed`;
  } else if (confirmed != null && measured != null) {
    secondary = `${formatCount(confirmed)} confirmed`;
  }

  const hourPart = hours != null ? `${formatHoursNumber(hours)} h usable` : null;
  let tertiary: string | null = null;
  if (hourPart && coverageLabel) {
    tertiary = `${hourPart} · ${coverageLabel}`;
  } else if (hourPart) {
    tertiary = hourPart;
  } else if (coverageLabel && primary !== coverageLabel) {
    tertiary = coverageLabel;
  }
  if (primary === 'No usable measurement' && tertiary === 'No usable measurement') {
    tertiary = null;
  }

  return {
    primary,
    secondary,
    tertiary,
    coverageLabel,
    verificationLabel,
    needsReview,
  };
}

export function dedupeBuyerLocationLabel(label: string | null | undefined): string {
  const parts = String(label || '')
    .split(/\s*\/\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  const unique: string[] = [];
  for (const part of parts) {
    if (!unique.some((existing) => existing.toLowerCase() === part.toLowerCase())) {
      unique.push(part);
    }
  }
  return unique.join(' / ');
}

export const OPEN_SCAN_ACTION_LABEL = 'Open Scan';
export const REVIEW_SCAN_ACTION_LABEL = 'Review';
export const VIEW_SCAN_ACTION_LABEL = 'View';
export const OPEN_SCAN_ACTION_ARIA = 'Open Safety Scan';
export const REVIEW_SCAN_ACTION_ARIA = 'Review Safety Scan';
export const VIEW_SCAN_ACTION_ARIA = 'View Safety Scan';

export type BuyerScanHistoryLike = {
  report_id?: string | null;
  reportId?: string | null;
  verification_state?: string | null;
  verificationState?: string | null;
  reviewed_observation_count?: number | null;
  reviewedOccurrenceCount?: number | null;
  scope_end?: string | null;
  scopeEnd?: string | null;
  window_end_utc?: string | null;
  signed_at?: string | null;
};

function reportIdOf(value: unknown): string {
  return String(value || '').trim();
}

export function isBuyerScanReviewComplete(args: {
  verificationState?: string | null;
  progressComplete?: boolean | null;
  unreviewed?: number | null;
}): boolean {
  const unreviewed = args.unreviewed;
  if (args.progressComplete === true && (unreviewed == null || unreviewed <= 0)) return true;
  return mapBuyerScanVerificationLabel(args.verificationState) === 'Verified';
}

/**
 * Buyer Customer Safety Scan PDF / Open target.
 * Prefer the latest review-complete Scan in the same case lineage.
 * Does not copy verified counts onto an empty rolling draft.
 */
export function resolveBuyerCustomerScanDownloadReportId(args: {
  openedReportId?: string | null;
  openedVerificationState?: string | null;
  openedProgressComplete?: boolean | null;
  openedUnreviewed?: number | null;
  scanHistory?: BuyerScanHistoryLike[] | null;
  latestSignedReportId?: string | null;
}): string {
  const opened = reportIdOf(args.openedReportId);
  if (opened && isBuyerScanReviewComplete({
    verificationState: args.openedVerificationState,
    progressComplete: args.openedProgressComplete,
    unreviewed: args.openedUnreviewed,
  })) {
    return opened;
  }
  const history = Array.isArray(args.scanHistory) ? args.scanHistory : [];
  const verified = history
    .map((row) => ({
      reportId: reportIdOf(row.report_id || row.reportId),
      verificationState: row.verification_state || row.verificationState || null,
      scopeEnd: String(row.scope_end || row.scopeEnd || row.window_end_utc || row.signed_at || ''),
    }))
    .filter((row) => (
      row.reportId
      && isBuyerScanReviewComplete({ verificationState: row.verificationState })
    ));
  verified.sort((left, right) => (
    (Date.parse(right.scopeEnd) || 0) - (Date.parse(left.scopeEnd) || 0)
  ));
  if (verified[0]?.reportId) return verified[0].reportId;
  const signed = reportIdOf(args.latestSignedReportId);
  if (signed) return signed;
  return opened;
}

export function buyerScanActionPresentation(args: {
  reportId?: string | null;
  isHistory?: boolean;
  needsReview?: boolean;
  workflowState?: string | null;
}): { actionLabel: string; actionAriaLabel: string } {
  if (args.workflowState === 'Failed') {
    return { actionLabel: 'Review Scan issue', actionAriaLabel: 'Review Safety Scan issue' };
  }
  if (args.isHistory) {
    return { actionLabel: VIEW_SCAN_ACTION_LABEL, actionAriaLabel: VIEW_SCAN_ACTION_ARIA };
  }
  if (args.needsReview) {
    return { actionLabel: REVIEW_SCAN_ACTION_LABEL, actionAriaLabel: REVIEW_SCAN_ACTION_ARIA };
  }
  return { actionLabel: OPEN_SCAN_ACTION_LABEL, actionAriaLabel: OPEN_SCAN_ACTION_ARIA };
}
